import { useQuery } from "@tanstack/react-query";
import type { LeverId } from "@helmr/contracts";
import { useSyncExternalStore } from "react";
import { getMe, hasLever, type Me } from "../api/me";
import { isApiError } from "../api/client";
import { useServices } from "./services";

export interface Context {
  status: "loading" | "ready" | "unavailable" | "error";
  me: Me | null;
  /**
   * D57: does the caller hold this lever? The same lever the server checks for the thing being shown.
   * While `unavailable` (no /me yet) every item shows; the server still refuses.
   */
  has: (lever: LeverId) => boolean;
  timeZone: string;
  locale: string;
  isOwner: boolean;
  retry: () => void;
}

const browserLocale = () => (typeof navigator !== "undefined" && navigator.language) || "en";
const browserZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useSessionSnapshot() {
  const { session } = useServices();
  return useSyncExternalStore(session.subscribe, session.getState, session.getState);
}

/** `GET /me` plus the permission helper `ctx.has(lever)` (D57). */
export function useAppContext(): Context {
  const { client } = useServices();
  const s = useSessionSnapshot();
  const q = useQuery({
    queryKey: ["me", s.businessId, s.branchId],
    queryFn: () => getMe(client),
    enabled: s.status === "scoped",
    retry: false,
    staleTime: 5 * 60_000,
  });
  const notConnected = isApiError(q.error) && q.error.status === 404;
  const me = q.data ?? null;
  // A failed background refresh keeps the levers we already hold.
  const status: Context["status"] = me ? "ready" : q.isPending ? "loading" : notConnected ? "unavailable" : q.isError ? "error" : "ready";
  return {
    status,
    me,
    has: (lever) => (status === "unavailable" ? true : hasLever(me, lever)),
    timeZone: me?.business.timezone ?? browserZone(),
    locale: me?.business.locale ?? browserLocale(),
    isOwner: me?.staff.isOwner ?? false,
    retry: () => void q.refetch(),
  };
}
